"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

const arabicDescription =
  "اختصارات فيجما الأساسية للمصممين — دليل سريع ومنظّم يساعدك على العمل بسرعة داخل Figma باستخدام اختصارات لوحة المفاتيح الأكثر استخدامًا.";

const SHORTCUTS_SOURCE_URL = "https://www.figmashortcuts.com/";

const shortcutSections = [
  {
    title: "Tools",
    items: [
      { actionEn: "Move", mac: "V", win: "V" },
      { actionEn: "Scale", mac: "K", win: "K" },
      { actionEn: "Frame", mac: "F or A", win: "F or A" },
      { actionEn: "Section", mac: "⇧ + S", win: "⇧ + S" },
      { actionEn: "Slice", mac: "S", win: "S" },
      { actionEn: "Rectangle", mac: "R", win: "R" },
      { actionEn: "Line", mac: "L", win: "L" },
      { actionEn: "Arrow", mac: "⇧ + L", win: "⇧ + L" },
      { actionEn: "Ellipse", mac: "O", win: "O" },
      { actionEn: "Place image", mac: "⇧ + ⌘ + K", win: "⇧ + Ctrl + K" },
      { actionEn: "Pen", mac: "P", win: "P" },
      { actionEn: "Pencil", mac: "⇧ + P", win: "⇧ + P" },
      { actionEn: "Text", mac: "T", win: "T" },
      { actionEn: "Resources", mac: "⇧ + I", win: "⇧ + I" },
      { actionEn: "Hand", mac: "H", win: "H" },
      { actionEn: "Add comment", mac: "C", win: "C" },
      { actionEn: "Pick color", mac: "I or Ctrl + C", win: "I or Ctrl + C" },
      { actionEn: "Quick actions", mac: "⌘ + , or ⌘ + P", win: "Ctrl + , or Ctrl + P" },
      { actionEn: "Press Return/Enter to perform an action", mac: "Enter", win: "Enter" },
      { actionEn: "Run last plugin", mac: "⌥ + ⌘ + P", win: "Alt + Ctrl + P" },
    ],
  },
  {
    title: "View",
    items: [
      { actionEn: "Show/Hide UI", mac: "\\ or ⌘ + .", win: "\\ or Ctrl + ." },
      { actionEn: "Minimize UI", mac: "⇧ + ⌘ + \\", win: "⇧ + Ctrl + \\" },
      { actionEn: "Multiplayer cursors", mac: "⌥ + ⌘ + \\", win: "Alt + Ctrl + \\" },
      { actionEn: "Rulers", mac: "⇧ + R", win: "⇧ + R" },
      { actionEn: "Show outlines", mac: "⇧ + O or ⌘ + Y", win: "Ctrl + Y" },
      { actionEn: "Pixel preview", mac: "⌘ + ⇧ + P", win: "Ctrl + ⇧ + P" },
      { actionEn: "Layout grids", mac: "⇧ + G", win: "⇧ + G" },
      { actionEn: "Pixel grid", mac: "⇧ + ' or ⌘ + '", win: "Ctrl + '" },
      { actionEn: "Collapse layers", mac: "⌥ + L", win: "Alt + L" },
      { actionEn: "Layers panel", mac: "⌥ + 1", win: "Alt + 1" },
      { actionEn: "Assets panel", mac: "⌥ + 2", win: "Alt + 2" },
      { actionEn: "Find and replace", mac: "⌘ + F", win: "Ctrl + F" },
      { actionEn: "Libraries", mac: "⌥ + 3", win: "Alt + 3" },
      { actionEn: "Design panel", mac: "⌥ + 8", win: "Alt + 8" },
      { actionEn: "Prototype panel", mac: "⌥ + 9", win: "Alt + 9" },
      { actionEn: "Inspect panel", mac: "⌥ + 0", win: "Alt + 0" },
      { actionEn: "Toggle design/prototype panel", mac: "⇧ + E", win: "⇧ + E" },
      { actionEn: "Show/Hide comments", mac: "⇧ + C", win: "⇧ + C" },
      { actionEn: "Show/Hide left panel", mac: "⇧ + ⌘ + \\", win: "⇧ + Ctrl + \\" },
      { actionEn: "Present (play the prototype)", mac: "⌥ + ⌘ + Enter", win: "Alt + Ctrl + Enter" },
      { actionEn: "Preview (play the prototype on the canvas)", mac: "⇧ + Space", win: "⇧ + Space" },
      { actionEn: "Switch between files", mac: "⌥ + ⌘ + ⬅️ ➡️", win: "Alt + Ctrl + ⬅️ ➡️" },
      { actionEn: "Switch between pages", mac: "fn + ⬇️ ⬆️", win: "PgUp / PgDown" },
      { actionEn: "Dev mode/Design mode", mac: "⇧ + D", win: "⇧ + D" },
    ],
  },
  {
    title: "Zoom",
    items: [
      { actionEn: "Zoom in", mac: "+", win: "+" },
      { actionEn: "Zoom out", mac: "-", win: "-" },
      { actionEn: "Zoom to 100%", mac: "⌘ + 0", win: "Ctrl + 0" },
      { actionEn: "Zoom to fit", mac: "⇧ + 1", win: "⇧ + 1" },
      { actionEn: "Zoom to selection", mac: "⇧ + 2", win: "⇧ + 2" },
      { actionEn: "Zoom to previous frame", mac: "⇧ + N", win: "⇧ + N" },
      { actionEn: "Zoom to next frame", mac: "N", win: "N" },
    ],
  },
  {
    title: "Text",
    items: [
      { actionEn: "Bold", mac: "⌘ + B", win: "Ctrl + B" },
      { actionEn: "Italic", mac: "⌘ + I", win: "Ctrl + I" },
      { actionEn: "Underline", mac: "⌘ + U", win: "Ctrl + U" },
      { actionEn: "Strikethrough", mac: "⇧ + ⌘ + X", win: "⇧ + Ctrl + X" },
      { actionEn: "Create link", mac: "⇧ + ⌘ + U", win: "⇧ + Ctrl + U" },
      { actionEn: "Bulleted list", mac: "⇧ + ⌘ + 8", win: "⇧ + Ctrl + 8" },
      { actionEn: "Numbered list", mac: "⇧ + ⌘ + 7", win: "⇧ + Ctrl + 7" },
      { actionEn: "Text align left", mac: "⌥ + ⌘ + L", win: "Alt + Ctrl + L" },
      { actionEn: "Text align center", mac: "⌥ + ⌘ + T", win: "Alt + Ctrl + T" },
      { actionEn: "Text align right", mac: "⌥ + ⌘ + R", win: "Alt + Ctrl + R" },
      { actionEn: "Text align justified", mac: "⌥ + ⌘ + J", win: "Alt + Ctrl + J" },
      { actionEn: "Increase indentation", mac: "⌘ + ]", win: "Ctrl + ]" },
      { actionEn: "Decrease indentation", mac: "⌘ + [", win: "Ctrl + [" },
      { actionEn: "Increase font size", mac: "⇧ + ⌘ + > or ⇧ + ⌘ + .", win: "⇧ + Ctrl + > or ⇧ + Ctrl + ." },
      { actionEn: "Decrease font size", mac: "⇧ + ⌘ + < or ⇧ + ⌘ + ,", win: "⇧ + Ctrl + <" },
      { actionEn: "Increase font weight", mac: "⌥ + ⌘ + > or ⌥ + ⌘ + .", win: "Alt + Ctrl + > or Alt + Ctrl + ." },
      { actionEn: "Decrease font weight", mac: "⌥ + ⌘ + < or ⌥ + ⌘ + ,", win: "Alt + Ctrl + < or Alt + Ctrl + ," },
      { actionEn: "Increase line height", mac: "⌥ + ⇧ + >", win: "Alt + ⇧ + >" },
      { actionEn: "Decrease line height", mac: "⌥ + ⇧ + <", win: "⌥ + ⇧ + <" },
      { actionEn: "Increase letter spacing", mac: "⌥ + > or ⌥ + .", win: "Alt + > or Alr + ," },
      { actionEn: "Decrease letter spacing", mac: "⌥ + < or ⌥ + ,", win: "Alt + < or Alt + ," },
    ],
  },
  {
    title: "Shape",
    items: [
      { actionEn: "Paint bucket", mac: "B", win: "B" },
      { actionEn: "Bend tool (while editing a shape)", mac: "⌘", win: "Ctrl" },
      { actionEn: "Remove fill", mac: "⌥ + /", win: "Alt + /" },
      { actionEn: "Remove stroke", mac: "⇧ + / or ⇧ + '", win: "⇧ + / or ⇧ + '" },
      { actionEn: "Swap fill and stroke", mac: "⇧ + X", win: "⇧ + X" },
      { actionEn: "Join selection", mac: "⌘ + J", win: "Ctrl + J" },
      { actionEn: "Smooth join selection (after selecting points)", mac: "⇧ + ⌘ + J", win: "⇧ + Ctrl + J" },
      { actionEn: "Delete and heal selection (after selecting points)", mac: "⇧ + Delete", win: "⇧ + Delete" },
      { actionEn: "Flatten selection", mac: "⌘ + E", win: "Ctrl + E" },
    ],
  },
  {
    title: "Selection",
    items: [
      { actionEn: "Deep select", mac: "⌘ + Click", win: "Ctrl + Click" },
      { actionEn: "Deep select with rectangle (while dragging to select)", mac: "⌘ + Drag", win: "Ctrl + Drag" },
      { actionEn: "Select layer menu", mac: "⌘ + Right click", win: "Ctrl + Right click" },
      { actionEn: "Select all", mac: "⌘ + A", win: "Ctrl + A" },
      { actionEn: "Select inverse", mac: "⇧ + ⌘ + A", win: "⇧ + Ctrl + A" },
      { actionEn: "Select none", mac: "Esc", win: "Esc" },
      { actionEn: "Select children", mac: "Enter", win: "Enter" },
      { actionEn: "Select parent", mac: "\\ or ⇧ + Enter", win: "\\ or ⇧ + Enter" },
      { actionEn: "Select next sibling", mac: "Tab", win: "Tab" },
      { actionEn: "Select previous sibling", mac: "⇧ + Tab", win: "⇧ + Tab" },
      { actionEn: "Select all matching layers", mac: "⌥ + ⌘ + A", win: "Alt + Ctrl + A" },
      { actionEn: "Group selection", mac: "⌘ + G", win: "Ctrl + G" },
      { actionEn: "Ungroup selection", mac: "⇧ + ⌘ + G", win: "⇧ + Ctrl + G" },
      { actionEn: "Frame selection", mac: "⌥ + ⌘ + G", win: "Alt + Ctrl + G" },
      { actionEn: "Show/Hide selection", mac: "⇧ + ⌘ + H", win: "⇧ + Ctrl + H" },
      { actionEn: "Lock/Unlock selection", mac: "⇧ + ⌘ + L", win: "⇧ + Ctrl + L" },
    ],
  },
  {
    title: "Cursor",
    items: [
      { actionEn: "Measure to selection (while pointing)", mac: "⌥", win: "Alt" },
      { actionEn: "Duplicate selection (while moving)", mac: "⌥", win: "Alt" },
      { actionEn: "Resize from center (while resizing)", mac: "⌥", win: "Alt" },
      { actionEn: "Resize proportionally (while resizing)", mac: "⇧", win: "⇧" },
      { actionEn: "Crop image (while resizing)", mac: "⌘", win: "Ctrl" },
      { actionEn: "Ignore constraints (frames) (while resizing)", mac: "⌘", win: "Ctrl" },
      { actionEn: "Move while resizing (while dragging)", mac: "Space", win: "Space" },
    ],
  },
  {
    title: "Edit",
    items: [
      { actionEn: "Copy", mac: "⌘ + C", win: "Ctrl + C" },
      { actionEn: "Cut", mac: "⌘ + X", win: "Ctrl + X" },
      { actionEn: "Paste", mac: "⌘ + V", win: "Ctrl + V" },
      { actionEn: "Paste to replace", mac: "⇧ + ⌘ + R", win: "⇧ + Ctrl + R" },
      { actionEn: "Paste over selection", mac: "⇧ + ⌘ + V", win: "⇧ + Ctrl + V" },
      { actionEn: "Duplicate", mac: "⌘ + D", win: "Ctrl + D" },
      { actionEn: "Rename", mac: "⌘ + R", win: "Ctrl + R" },
      { actionEn: "Export", mac: "⇧ + ⌘ + E", win: "⇧ + Ctrl + E" },
      { actionEn: "Copy as PNG", mac: "⇧ + ⌘ + C", win: "⇧ + Ctrl + C" },
      { actionEn: "Copy properties", mac: "⌥ + ⌘ + C", win: "Alt + Ctrl + C" },
      { actionEn: "Paste properties", mac: "⌥ + ⌘ + V", win: "Alt + Ctrl + V" },
    ],
  },
  {
    title: "Transform",
    items: [
      { actionEn: "Flip horizontal", mac: "⇧ + H", win: "⇧ + H" },
      { actionEn: "Flip vertical", mac: "⇧ + V", win: "⇧ + V" },
      { actionEn: "Use as mask", mac: "⌃ + ⌘ + M", win: "⌃ + Ctrl + M" },
      { actionEn: "Edit shape or image", mac: "Enter", win: "Enter" },
      { actionEn: "Resize the frame to fit", mac: "⌥ + ⇧ + ⌘ + R", win: "Alt + ⇧ + Ctrl + R" },
      { actionEn: "Set opacity to 0%", mac: "00", win: "00" },
      { actionEn: "Set opacity to 10%", mac: "1", win: "1" },
      { actionEn: "Set opacity to 20%", mac: "2", win: "2" },
      { actionEn: "Set opacity to 30%", mac: "3", win: "3" },
      { actionEn: "Set opacity to 40%", mac: "4", win: "4" },
      { actionEn: "Set opacity to 50%", mac: "5", win: "5" },
      { actionEn: "Set opacity to 60%", mac: "6", win: "6" },
      { actionEn: "Set opacity to 70%", mac: "7", win: "7" },
      { actionEn: "Set opacity to 80%", mac: "8", win: "8" },
      { actionEn: "Set opacity to 90%", mac: "9", win: "9" },
      { actionEn: "Set opacity to 100%", mac: "0", win: "0" },
      { actionEn: "Set opacity to specific value", mac: "Enter the number", win: "Enter the number" },
    ],
  },
  {
    title: "Arrange",
    items: [
      { actionEn: "Align left", mac: "⌥ + A", win: "Alt + A" },
      { actionEn: "Align horizontal centers", mac: "⌥ + H", win: "Alt + H" },
      { actionEn: "Align right", mac: "⌥ + D", win: "Alt + D" },
      { actionEn: "Align top", mac: "⌥ + W", win: "Alt + W" },
      { actionEn: "Align vertical centers", mac: "⌥ + V", win: "Alt + V" },
      { actionEn: "Align bottom", mac: "⌥ + S", win: "Alt + S" },
      { actionEn: "Tidy up", mac: "Ctrl + ⌥ + T", win: "Ctrl + Alt + T" },
      { actionEn: "Distribute horizontal spacing", mac: "Ctrl + ⌥ + H", win: "Ctrl + Alt + H" },
      { actionEn: "Distribute vertical spacing", mac: "Ctrl + ⌥ + V", win: "Ctrl + Alt + V" },
      { actionEn: "Bring to front", mac: "⌥ + ⌘ + ]", win: "Ctrl + ⇧ + ]" },
      { actionEn: "Bring forward", mac: "⌘ + ]", win: "Ctrl + ]" },
      { actionEn: "Send backward", mac: "⌘ + [", win: "Ctrl + [" },
      { actionEn: "Send to back", mac: "⌥ + ⌘ + [", win: "Ctrl + ⇧ + [" },
    ],
  },
  {
    title: "Components",
    items: [
      { actionEn: "Show assets", mac: "⌥ + 2", win: "Alt + 2" },
      { actionEn: "Libraries", mac: "⌥ + ⌘ + O", win: "Alt + Ctrl + O" },
      { actionEn: "Create component", mac: "⌥ + ⌘ + K", win: "Alt + Ctrl + K" },
      { actionEn: "Detach instance", mac: "⌥ + ⌘ + B", win: "Alt + Ctrl + B" },
      { actionEn: "Swap component instance (while inserting a component)", mac: "⌥", win: "Alt" },
      { actionEn: "Go to main component", mac: "Ctrl + ⌥ + ⌘ + K", win: "Shift + Alt + Ctrl + K" },
    ],
  },
  {
    title: "Auto layout",
    items: [
      { actionEn: "Add auto layout", mac: "⇧ + A", win: "⇧ + A" },
      { actionEn: "Remove auto layout", mac: "⌥ + ⇧ + A", win: "Alt + ⇧ + A" },
      { actionEn: "Suggest auto layout", mac: "Ctrl + ⇧ + A", win: "Ctrl + Alt + ⇧ + A" },
      { actionEn: "Drag padding/spacing with big nudge steps (while dragging handles)", mac: "⇧ + Drag", win: "⇧ + Drag" },
      { actionEn: "Drag padding from both sides (while dragging handles)", mac: "⌥ + Drag", win: "Alt + Drag" },
      { actionEn: "Drag padding from all sides (while dragging handles)", mac: "⌥ + ⇧ + Drag", win: "Alt + ⇧ + Drag" },
      { actionEn: "Edit both padding sides (click padding area)", mac: "⌥ + Click", win: "Alt + Click" },
      { actionEn: "Edit all padding sides (click padding area)", mac: "⌥ + ⇧ + Click", win: "Alt + ⇧ + Click" },
      { actionEn: "Single padding field (while clicking into any padding fields in design panel)", mac: "⌘", win: "Ctrl" },
      { actionEn: "Set hug contents", mac: "Double click Vertical or Horizontal edge", win: "Double click Vertical or Horizontal edge" },
      { actionEn: "Set fill container", mac: "⌥ + Double click Vertical or Horizontal edge", win: "Alt + Double click Vertical or Horizontal edge" },
      { actionEn: "Set alignment stepwise (after clicking on alignment box)", mac: "↓ → ↑ ←", win: "↓ → ↑ ←" },
      { actionEn: "Set alignment to edge (after clicking on alignment box)", mac: "W A S D", win: "W A S D" },
      { actionEn: "Set alignment to center vertically/horizontally (after clicking on alignment box)", mac: "V H", win: "V H" },
      { actionEn: "Toggle text baseline alignment (after clicking on alignment box)", mac: "B", win: "B" },
      { actionEn: "Toggle space between/packed (after clicking on alignment box)", mac: "X or Double click on alignment widget", win: "X or Double click on alignment widget" },
      { actionEn: "Open Menu to allow toggling (with Width/Height in focus)", mac: "⌘ + ↓", win: "Ctrl + ↓" },
    ],
  },
];

export default function FigmaShortcutsPage() {
  const [mounted, setMounted] = useState(false);
  const [platform, setPlatform] = useState("mac"); // mac | win
  const [query, setQuery] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return shortcutSections;

    return shortcutSections
      .map((section) => {
        const items = section.items.filter((item) => {
          const haystack = `${item.actionEn} ${item.mac} ${item.win}`.toLowerCase();
          return haystack.includes(q);
        });
        return { ...section, items };
      })
      .filter((section) => section.items.length > 0);
  }, [query]);

  const totalCount = useMemo(
    () => filteredSections.reduce((acc, s) => acc + s.items.length, 0),
    [filteredSections]
  );

  return (
    <main className="container py-5" dir="rtl">
      <div className="text-center mb-5">
        <h1 className="display-5 fw-bold mb-3" dir="ltr">
      إختصارات فيجما
         {/* Figma Shortcuts */}
        </h1>
        <p
          className="lead text-muted mx-auto"
          style={{ maxWidth: "760px" }}
          suppressHydrationWarning
        >
          {mounted ? arabicDescription : null}
        </p>

        <div className="d-flex justify-content-center gap-2 flex-wrap mt-3">
          <Link href="/services" className="btn btn-outline-dark rounded-3">
            العودة للخدمات
          </Link>
          {/* <a
            href={SHORTCUTS_SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-dark rounded-3"
          >
            مصدر الاختصارات
          </a> */}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
        <div className="card-body p-4">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-lg-6">
              <label className="form-label mb-1">البحث</label>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="form-control rounded-3"
                placeholder="ابحث عن اختصار أو أمر… (مثال: Copy, Auto layout, ⌘)"
              />
              <div className="text-muted small mt-2">
                النتائج: <b className="text-dark">{totalCount}</b>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <label className="form-label mb-1">النظام</label>
              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className={`btn rounded-3 ${platform === "mac" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setPlatform("mac")}
                >
                  Mac
                </button>
                <button
                  type="button"
                  className={`btn rounded-3 ${platform === "win" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setPlatform("win")}
                >
                  Windows
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => setQuery("")}
                  disabled={!query.trim()}
                >
                  مسح البحث
                </button>
              </div>
              <div className="text-muted small mt-2">
                ملاحظة: قد تختلف بعض الاختصارات حسب نوع لوحة المفاتيح.
              </div>
            </div>
          </div>
        </div>
      </div>

      {filteredSections.length === 0 ? (
        <div className="text-center py-5">
          <h3 className="h5 fw-bold mb-2">لا توجد نتائج</h3>
          <p className="text-muted mb-0">جرّب كلمة بحث أخرى.</p>
        </div>
      ) : (
        <div className="row row-cols-1 row-cols-lg-2 g-4">
          {filteredSections.map((section) => (
            <div className="col" key={section.title}>
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-bold mb-0" dir="ltr">
                      {section.title}
                    </h5>
                    <span className="badge bg-light text-dark border" dir="ltr">
                      {section.items.length}
                    </span>
                  </div>

                  <div className="d-grid gap-2">
                    {section.items.map((item) => {
                      const keys = platform === "mac" ? item.mac : item.win;
                      return (
                        <div
                          key={`${section.title}-${item.actionEn}-${keys}`}
                          className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2"
                        >
                          <span className="text-muted small" dir="ltr">
                            {item.actionEn}
                          </span>
                          <code
                            className="px-2 py-1 rounded-2"
                            style={{ background: "rgba(0,0,0,0.06)" }}
                            dir="ltr"
                          >
                            {keys}
                          </code>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

